import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import {
  InformationPackageApiProvider,
  useInformationPackageApi,
} from '../Services/InformationPackageApiProvider';
import type { InformationPackageListItemApi } from '../Services/InformationPackageApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

interface InformationPackageProps {
  canEdit?: boolean;
}

const columns = [
  { name: 'ID', selector: (row: InformationPackageListItemApi) => row.key, sortable: false },
  { name: 'Name', selector: (row: InformationPackageListItemApi) => row.name, sortable: false },
  {
    name: 'Status',
    selector: (row: InformationPackageListItemApi) => (row.is_active ? 'Active' : 'InActive'),
    sortable: false,
  },
  {
    name: 'Created',
    selector: (row: InformationPackageListItemApi) => formatDateUTC(row.created_at),
    sortable: false,
  },
  {
    name: 'Updated',
    selector: (row: InformationPackageListItemApi) => formatDateUTC(row.updated_at),
    sortable: false,
  },
];

function InformationPackageContent({ canEdit }: InformationPackageProps) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getInformationPackages, deleteInformationPackage } = useInformationPackageApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [informationPackages, setInformationPackages] = useState<InformationPackageListItemApi[]>(
    [],
  );
  const [totalInformationPackages, setTotalInformationPackages] = useState(0);

  const tableTitle = 'Information Packages';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(tableTitle),
  );

  const fetchInformationPackages = async (skip: number, limit: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await getInformationPackages(skip, limit);
      setInformationPackages(response.items);
      setTotalInformationPackages(response.totalCount);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch information packages!');
    } finally {
      setLoading(false);
    }
  };

  const onDelete = (selectedIds: (string | number)[]) => {
    const selectedId = selectedIds[0];

    if (!selectedId) {
      return;
    }

    showConfirmation('Are you sure you want to delete the selected item?', async () => {
      try {
        await deleteInformationPackage(String(selectedId));
        showAlert('success', 'Item was successfully deleted!');
        fetchInformationPackages(persistedSkip, persistedPageSize);
      } catch {
        showAlert('fail', 'Failed to delete the item!');
      }
    });
  };

  const onOpen = (row: InformationPackageListItemApi) => {
    navigate(`/organizations/${organizationId}/information-package/${row.key}`, {
      state: { organizationId },
    });
  };

  useEffect(() => {
    fetchInformationPackages(persistedSkip, persistedPageSize);
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={informationPackages}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={persistedPageSize}
                paginationTotalRows={totalInformationPackages}
                onRowClicked={onOpen}
                sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                onDelete={onDelete}
                onChangePage={fetchInformationPackages}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(
                      `/organizations/${organizationId}/information-package/new-information-package`,
                    )
                  }
                >
                  Create New Information Package
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InformationPackage({ canEdit }: InformationPackageProps) {
  return (
    <ConfirmationProvider>
      <InformationPackageApiProvider>
        <InformationPackageContent canEdit={canEdit} />
      </InformationPackageApiProvider>
    </ConfirmationProvider>
  );
}

export default InformationPackage;
